// src/services/roleInvitationService.js
import { 
  collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../config/firebase';

class RoleInvitationService {
  // Send role invitation (creates pb-role-invitations doc + notification)
  async sendRoleInvitation({ bookId, bookName, targetEmail, targetUserId, role }) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    // Check for existing pending invitation
    const existing = await this.checkExistingInvitation(bookId, targetEmail);
    if (existing) {
      throw new Error('Invitation already pending for this user');
    }

    const invitationData = {
      bookId,
      bookName,
      targetEmail,
      targetUserId: targetUserId || null,
      role,
      inviterUserId: currentUser.uid,
      inviterEmail: currentUser.email,
      inviterName: currentUser.displayName || currentUser.email,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
    };

    // Create invitation
    const invRef = await addDoc(collection(db, 'pb-role-invitations'), invitationData);

    // If user exists, create in-app notification
    if (targetUserId) {
      await addDoc(collection(db, 'pb-notifications'), {
        userId: targetUserId,
        type: 'role_invitation',
        category: 'invitation',
        title: 'Role Invitation',
        message: `${invitationData.inviterName} invited you to be a ${role} for "${bookName}"`,
        data: {
          invitationId: invRef.id,
          bookId,
          bookName,
          role
        },
        read: false,
        actionRequired: true,
        priority: 'high',
        createdAt: serverTimestamp(),
        expiresAt: invitationData.expiresAt
      });
    } else {
      // User not registered - send email via Cloud Function
      const sendEmailInvite = httpsCallable(functions, 'sendRoleInvitationEmail');
      await sendEmailInvite({ invitationId: invRef.id });
    }

    return invRef.id;
  }

  async checkExistingInvitation(bookId, email) {
    const q = query(
      collection(db, 'pb-role-invitations'),
      where('bookId', '==', bookId),
      where('targetEmail', '==', email),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  async getPendingInvitations(bookId) {
    const q = query(
      collection(db, 'pb-role-invitations'),
      where('bookId', '==', bookId),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  async getUserPendingInvitations(userId, email) {
    // Get invitations by userId or email
    const byUserId = query(
      collection(db, 'pb-role-invitations'),
      where('targetUserId', '==', userId),
      where('status', '==', 'pending')
    );
    
    const byEmail = query(
      collection(db, 'pb-role-invitations'),
      where('targetEmail', '==', email),
      where('status', '==', 'pending')
    );

    const [userSnap, emailSnap] = await Promise.all([
      getDocs(byUserId),
      getDocs(byEmail)
    ]);

    const invitations = new Map();
    userSnap.docs.forEach(d => invitations.set(d.id, { id: d.id, ...d.data() }));
    emailSnap.docs.forEach(d => invitations.set(d.id, { id: d.id, ...d.data() }));

    return Array.from(invitations.values());
  }

  async acceptInvitation(invitationId) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    // Update invitation status - Cloud Function will set claims
    await updateDoc(doc(db, 'pb-role-invitations', invitationId), {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      acceptedByUserId: currentUser.uid
    });

    // Force token refresh to get new claims
    await currentUser.getIdToken(true);
  }

  async declineInvitation(invitationId) {
    await updateDoc(doc(db, 'pb-role-invitations', invitationId), {
      status: 'declined',
      declinedAt: serverTimestamp()
    });
  }

  async cancelInvitation(invitationId) {
    await deleteDoc(doc(db, 'pb-role-invitations', invitationId));
  }

  // Super admin direct assignment
  async directAssignBookRole(userId, bookId, role) {
    const addRoleFn = httpsCallable(functions, 'addBookRole');
    return addRoleFn({ targetUid: userId, bookId, role });
  }

  async directAssignSystemRole(userId, role) {
    const setClaimsFn = httpsCallable(functions, 'setUserClaims');
    return setClaimsFn({ uid: userId, claims: { role } });
  }
}

export const roleInvitationService = new RoleInvitationService();
export default roleInvitationService;
